import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getPortfolios, pickFreeTierPortfolios } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/components/ui/sonner";
import { getApiErrorMessage } from "@/lib/api";
import { Check } from "lucide-react";
import { useT } from "@/lib/translation";

/** How many portfolios the free plan keeps editable. Mirrors FREE_MAX_PORTFOLIOS. */
export const FREE_ACTIVE_PORTFOLIOS = 2;

function joinNames(names: string[], conjunction: string): string {
  if (names.length <= 1) return names.join("");
  return `${names.slice(0, -1).join(", ")} ${conjunction} ${names[names.length - 1]}`;
}

/**
 * Shown by BillingGate when a free user has more portfolios than the free plan allows and
 * fewer picked than the allowance. That is either no pick yet, or a picked portfolio that
 * was deleted while others are still locked. In the second case the portfolios still picked
 * (the unlocked ones) stay in the choice and the user only fills the free slots. The pick is
 * final for this stay on the free plan: it only clears when the user subscribes again.
 */
export default function FreeTierPortfolioPicker() {
  const queryClient = useQueryClient();
  const t = useT();
  const [selected, setSelected] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);

  const {
    data: portfolios,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["portfolios"],
    queryFn: getPortfolios,
  });

  const choices = (portfolios ?? []).filter(
    (p) => !p.is_combined && !p.is_demo,
  );
  // While the picker shows, the unlocked portfolios are the ones still picked. The backend
  // requires them in the new choice, so they are always selected and cannot be removed.
  const kept = choices.filter((p) => !p.is_locked);
  const keptIds = kept.map((p) => p.id);
  const chosen = [
    ...keptIds,
    ...selected.filter((id) => !keptIds.includes(id)),
  ];
  const remaining = FREE_ACTIVE_PORTFOLIOS - kept.length;

  const toggle = (id: number) => {
    if (keptIds.includes(id)) return;
    setSelected((current) => {
      if (current.includes(id)) return current.filter((x) => x !== id);
      if (keptIds.length + current.length >= FREE_ACTIVE_PORTFOLIOS)
        return current;
      return [...current, id];
    });
  };

  const handleConfirm = async () => {
    if (saving || chosen.length !== FREE_ACTIVE_PORTFOLIOS) return;
    setSaving(true);
    try {
      // The backend rebuilds the "All portfolios" rollup before answering, so refresh the
      // same data the Dashboard refreshes after a rollup change.
      await pickFreeTierPortfolios(chosen);
      queryClient.removeQueries({ queryKey: ["holdings"] });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["billing-info-status"] }),
        queryClient.invalidateQueries({ queryKey: ["portfolios"] }),
        queryClient.invalidateQueries({ queryKey: ["historicalValue"] }),
      ]);
    } catch (err) {
      toast.error(t("billing:picker.saveFailed"), {
        description: getApiErrorMessage(
          err,
          t("billing:toasts.tryAgainShortly"),
        ),
      });
      // A refusal can mean the choice was already made elsewhere, so reload the gate.
      queryClient
        .invalidateQueries({ queryKey: ["billing-info-status"] })
        .catch(() => {});
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) return null;

  if (isError && !portfolios) {
    return (
      <div className="flex h-full min-h-[70vh] items-center justify-center">
        <Card className="max-w-xl w-full">
          <CardHeader>
            <CardTitle>{t("billing:picker.title")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5 text-sm">
            <p>{t("billing:picker.loadError")}</p>
            <Button className="w-full" onClick={() => void refetch()}>
              {t("common:actions.tryAgain")}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isTopUp = kept.length > 0;

  return (
    <div className="flex h-full min-h-[70vh] items-center justify-center">
      <Card className="max-w-xl w-full">
        <CardHeader>
          <CardTitle>
            {isTopUp
              ? t("billing:picker.titleTopUp", { count: remaining })
              : t("billing:picker.titleInitial", {
                  limit: FREE_ACTIVE_PORTFOLIOS,
                })}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5 text-sm">
          {isTopUp ? (
            <p>
              {t("billing:picker.bodyTopUp", {
                count: remaining,
                limit: FREE_ACTIVE_PORTFOLIOS,
                names: joinNames(
                  kept.map((p) => p.name),
                  t("common:words.and"),
                ),
              })}
            </p>
          ) : (
            <p>
              {t("billing:picker.bodyInitial", {
                limit: FREE_ACTIVE_PORTFOLIOS,
                total: choices.length,
              })}
            </p>
          )}

          <div className="flex flex-col gap-2">
            {choices.map((p) => {
              const isKept = keptIds.includes(p.id);
              const isSelected = chosen.includes(p.id);
              const isFull =
                !isSelected && chosen.length >= FREE_ACTIVE_PORTFOLIOS;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => toggle(p.id)}
                  aria-pressed={isSelected}
                  disabled={isKept || isFull}
                  className={`flex items-center justify-between rounded-xl border p-4 text-left transition-colors ${
                    isSelected
                      ? "border-primary bg-primary/5 ring-1 ring-primary"
                      : "border-border hover:border-primary/40"
                  } disabled:cursor-not-allowed ${
                    isKept
                      ? ""
                      : "disabled:opacity-50 disabled:hover:border-border"
                  }`}
                >
                  <span className="min-w-0">
                    <span className="block truncate font-medium text-foreground">
                      {p.name}
                    </span>
                    {p.brokerage && (
                      <span className="block text-xs text-muted-foreground capitalize">
                        {p.brokerage}
                      </span>
                    )}
                  </span>
                  {isSelected && (
                    <Check className="h-4 w-4 shrink-0 text-primary" />
                  )}
                </button>
              );
            })}
          </div>

          <p className="text-muted-foreground">{t("billing:picker.stays")}</p>

          <Button
            className="w-full"
            disabled={saving || chosen.length !== FREE_ACTIVE_PORTFOLIOS}
            onClick={() => void handleConfirm()}
          >
            {saving
              ? t("billing:actions.saving")
              : t("billing:picker.confirm", { limit: FREE_ACTIVE_PORTFOLIOS })}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
